// eslint:disable max-func-body-length
import React, { useEffect } from 'react';
import { DateTime, MeatballMenu, SectionPanel } from 'ui';
import { SelectMultiple, SelectSingle } from 'app/area/shared/translated-components';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { parseAsDateUTC, createUTCDate, dateToUTC, createFormatters, isDisabled } from 'app/infrastructure/data/date';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { FavouriteSectionType } from '../metroline/types';
import { FavouriteFormIds } from '../constants';
import { FavouriteSection } from '../types';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { endpoints } from '../endpoints';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { maxFavoriteIdForOwner } from 'app/area/favourite-administration/constants';
import {
  disabledValidation, disabledWarning, favouriteLinkWithParams, favouriteTypeReadOnly,
  isOwnerReadOnly, ownerValidation, parentValidation
} from 'app/area/favourite-administration/form/components/helpers';
import { LinkField } from 'ui/common/link-field/link-field';
import { getFieldId } from 'app/area/search-filters/filters/selectors';
import { fieldType, filterId } from 'app/area/search-filters/constants';
import styles from './favourite-details.scss';

type ParentFieldProps = Pick<FavouriteSection, 'favouriteItem' | 'validation' | 'translation' | 'clearParent'>;

const ParentField: React.FC<ParentFieldProps> = ({ favouriteItem, validation, translation, clearParent }) => {
  const hasParent = !!favouriteItem?.parent;

  return <LinkField
    componentId={FavouriteFormIds.parent}
    label={translation.parent}
    text={favouriteItem?.parent?.description}
    linkUrl={hasParent
      && favouriteLinkWithParams(getFieldId(filterId.favouriteId, fieldType.root), favouriteItem?.ancestors)}
    validation={parentValidation(favouriteItem, validation?.[FavouriteFormIds.parent], translation)}>
    {hasParent
      && <MeatballMenu className={styles.parentMeatball}>
        <li>
          <button type="button" className="dropdown-item" onClick={clearParent}>
            {translation.clear_parent}
          </button>
        </li>
      </MeatballMenu>}
  </LinkField>;
};

type OwnersFieldProps = Pick<FavouriteSection,
  'favouriteItem' | 'originalFavouriteItem' | 'options' | 'locale' | 'validation' | 'translation' | 'updateItem' | 'loadOptions'>;

const OwnersField: React.FC<OwnersFieldProps> = ({
  favouriteItem, originalFavouriteItem, options, locale, validation, translation, updateItem, loadOptions
}) => {
  const onOwnerChange = (owners: ComplexValue<number>[]) => {
    updateItem({ owners });
  };

  const onOwnerLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(
      FavouriteFormIds.owners,
      endpoints.ownerOptions,
      loadOptionsParams,
      { ...(!!favouriteItem?.parent?.value && { favouriteId: favouriteItem?.id }) }
    );
  };

  return <SelectMultiple
    componentId={FavouriteFormIds.owners}
    className={undefined}
    locale={locale}
    valueItems={favouriteItem?.owners || []}
    label={{ text: translation.owner, visible: true, required: favouriteItem?.id >= maxFavoriteIdForOwner }}
    readOnly={isOwnerReadOnly(favouriteItem?.id)}
    options={options?.[FavouriteFormIds.owners]?.codes as ComplexValue<number>[] || []}
    placeholder={translation?.search}
    validation={
      ownerValidation(
        favouriteItem,
        originalFavouriteItem,
        validation?.[FavouriteFormIds.owners],
        translation
      )
    }
    fetching={options?.[FavouriteFormIds.owners]?.fetching}
    onLoadOptions={onOwnerLoadOptions}
    onChange={onOwnerChange}
    displaySelectAll={true} />;
};

type FavouriteTypeFieldProps = Pick<FavouriteSection,
  'favouriteItem' | 'originalFavouriteItem' | 'options' | 'validation' | 'translation' | 'updateItem' | 'loadOptions'>;

const FavouriteTypeField: React.FC<FavouriteTypeFieldProps> = ({
  favouriteItem, originalFavouriteItem, options, validation, translation, updateItem, loadOptions
}) => {
  const isReadOnly = favouriteTypeReadOnly(originalFavouriteItem?.favouriteType?.value);

  const onReportTypeChange = (favouriteType: ComplexValue<number>) => {
    updateItem({ favouriteType });
  };

  const onFavouriteTypeLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(FavouriteFormIds.reportType, endpoints.favouriteTypeOptions, loadOptionsParams);
  };

  return <SelectSingle
    componentId={FavouriteFormIds.favouriteType}
    className={undefined}
    valueItem={isDefinedIncludingZero(favouriteItem?.favouriteType?.value) && favouriteItem.favouriteType}
    label={{ text: translation.favourite_type, visible: true, required: !isReadOnly }}
    readOnly={isReadOnly}
    options={options?.[FavouriteFormIds.reportType]?.codes as ComplexValue<number>[] || []}
    placeholder={translation?.search}
    validation={validation?.[FavouriteFormIds.favouriteType]}
    fetching={options?.[FavouriteFormIds.reportType]?.fetching}
    onLoadOptions={onFavouriteTypeLoadOptions}
    onChange={onReportTypeChange} />;
};

type DisabledFieldProps = Pick<FavouriteSection, 'favouriteItem' | 'locale' | 'validation' | 'translation' | 'updateItem'>;

const DisabledField: React.FC<DisabledFieldProps> = ({ favouriteItem, locale, validation, translation, updateItem }) => {
  const isDisabledParent = isDisabled(favouriteItem?.parent?.disabled);

  const onDisabledChange = (value: Date) => {
    updateItem({ disabled: value });
  };

  return <DateTime
    componentId={FavouriteFormIds.disabledId}
    className="dateTime"
    value={favouriteItem?.disabled}
    label={{ text: translation.disabled_removed }}
    info={{ text: translation.disabled_tooltip, visible: true }}
    readOnly={isDisabledParent}
    useTime={false}
    parseDate={parseAsDateUTC}
    createDate={createUTCDate}
    cloneDate={dateToUTC}
    {...createFormatters(locale)}
    locale={locale}
    onChange={onDisabledChange}
    captionWrapper={'form'}
    validation={disabledValidation(
      isDisabledParent,
      disabledWarning(favouriteItem?.favouriteType?.value, favouriteItem?.disabled),
      validation?.[FavouriteFormIds.disabledId],
      translation)} />;
};

export const FavouriteDetails: React.FC<FavouriteSection> = ({
  favouriteItem, originalFavouriteItem, options, updateItem, locale, validation, translation, section,
  loadOptions, getRef, shouldRenderSection, clearParent, hideRemoveSchedule
}) => {

  useEffect(() => {
    if (hideRemoveSchedule) {
      updateItem({ scheduleOwner: undefined, hasSchedule: false });
    }
  }, [hideRemoveSchedule]);

  return <MetroLineSection<FavouriteSectionType>
    section={section}
    sectionType={FavouriteSectionType.Details}
    getRef={getRef}
    shouldRenderSection={shouldRenderSection}>
    <div ref={getRef(FavouriteSectionType.Details)} className={styles.favouriteDetails}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId={FavouriteSectionType.Details}
        validation={undefined}
        info={{ text: translation.details }}
        label={{ visible: true, text: translation.details }} />
      <MainGridContainer>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <ParentField
              favouriteItem={favouriteItem}
              validation={validation}
              translation={translation}
              clearParent={clearParent} />
          </div>
          <div className="col-lg-6 col-sm-12">
            <DisabledField
              favouriteItem={favouriteItem}
              locale={locale}
              validation={validation}
              translation={translation}
              updateItem={updateItem} />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <FavouriteTypeField
              favouriteItem={favouriteItem}
              originalFavouriteItem={originalFavouriteItem}
              options={options}
              validation={validation}
              translation={translation}
              updateItem={updateItem}
              loadOptions={loadOptions} />
          </div>
          <div className="col-lg-6 col-sm-12">
            <OwnersField
              favouriteItem={favouriteItem}
              originalFavouriteItem={originalFavouriteItem}
              options={options}
              locale={locale}
              validation={validation}
              translation={translation}
              updateItem={updateItem}
              loadOptions={loadOptions} />
          </div>
        </div>
      </MainGridContainer>
    </div>
  </MetroLineSection>;
};
